import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAlert } from '../contexts/AlertContext';
import { useNavigate } from 'react-router-dom';

interface UseOTPAuthReturn {
    email: string;
    setEmail: (email: string) => void;
    otp: string;
    setOtp: (otp: string) => void;
    currentStep: number;
    isLoading: boolean;
    sendCode: () => Promise<void>;
    verifyCode: () => Promise<void>;
    resendCode: () => Promise<void>;
    backToEmail: () => void;
}

export const useOTPAuth = (): UseOTPAuthReturn => {
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState('');
    const [currentStep, setCurrentStep] = useState(1);
    const { sendLoginCode, login, isLoading } = useAuth();
    const { showAlert } = useAlert();
    const navigate = useNavigate();

    const isValidEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const sendCode = async (): Promise<void> => {
        if (!email.trim()) {
            showAlert('Please enter your email address', 'error');
            return;
        }

        if (!isValidEmail(email)) {
            showAlert('Please enter a valid email address', 'error');
            return;
        }

        try {
            await sendLoginCode(email);
            showAlert('Login code sent to your email!', 'success', 5000);
            setCurrentStep(2);
        } catch (error: any) {
            showAlert(error.message || 'Failed to send login code', 'error');
        }
    };

    const verifyCode = async (): Promise<void> => {
        if (!otp.trim() || otp.length !== 6) {
            showAlert('Please enter the 6-digit code', 'error');
            return;
        }

        try {
            await login(email, otp);
            showAlert('<center>Access granted!</center><br>Redirecting you to customer selection...', 'success');
            setTimeout(() => navigate('/customers'), 1000);
        } catch (error: any) {
            showAlert(error.message || 'Invalid code. Please try again.', 'error');
        }
    };

    const resendCode = async (): Promise<void> => {
        await sendCode();
    };

    const backToEmail = (): void => {
        setCurrentStep(1);
        setOtp('');
    };

    return {
        email,
        setEmail,
        otp,
        setOtp,
        currentStep,
        isLoading,
        sendCode,
        verifyCode,
        resendCode,
        backToEmail,
    };
};